/**
 * GridCard — White card with health accent strip, monogram circle,
 * centered price, $/hr, title, and metadata. Clean Canvas Sage aesthetic.
 */

import { useRef, useState } from 'react';
import { T } from '@/styles/tokens';
import { getHealth } from '@/lib/portfolioHealth';
import { relativeTime } from '@/lib/relativeTime';
import type { HealthStatus } from '@/lib/portfolioHealth';
import type { VerdictSummary } from '@/types';

const HEALTH_ACCENT: Record<HealthStatus, string> = {
  above: T.primary,
  near: '#D9955C',
  below: '#C45A5A',
  sold: T.textMuted,
};

interface GridCardProps {
  item: VerdictSummary;
  floorHourly: number;
  currentBurn?: number;
  index: number;
  visible: boolean;
  selectionMode: boolean;
  selected: boolean;
  onToggle: () => void;
  onLongPress: () => void;
  onTap: () => void;
}

function displayPrice(item: VerdictSummary): string {
  const cents = item.sold && item.soldPrice ? item.soldPrice : (item.gutPrice ?? item.ttcp);
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

export function GridCard({
  item, floorHourly, currentBurn, index, visible,
  selectionMode, selected, onToggle, onLongPress, onTap,
}: GridCardProps) {
  const longTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const moved = useRef(false);
  const [isPressed, setIsPressed] = useState(false);

  const down = () => {
    setIsPressed(true);
    if (selectionMode) return;
    moved.current = false;
    longTimer.current = setTimeout(() => {
      if (!moved.current) onLongPress();
    }, 450);
  };
  const up = () => { setIsPressed(false); if (longTimer.current) clearTimeout(longTimer.current); };
  const mv = () => { moved.current = true; if (longTimer.current) { clearTimeout(longTimer.current); longTimer.current = null; } };
  const click = () => { if (selectionMode) { onToggle(); return; } onTap(); };

  const h: HealthStatus = getHealth(item, floorHourly);
  const accent = HEALTH_ACCENT[h];
  const title = item.title || `${item.width}\u00D7${item.height}"`;

  return (
    <div
      className="tp-card"
      onPointerDown={down}
      onPointerUp={up}
      onPointerLeave={up}
      onPointerMove={mv}
      onContextMenu={e => e.preventDefault()}
      onClick={click}
      style={{
        position: 'relative',
        background: T.white,
        borderRadius: 12,
        overflow: 'hidden',
        border: selected ? `2px solid ${T.primary}` : '1px solid rgba(0,0,0,0.06)',
        minWidth: 0,
        cursor: 'pointer',
        opacity: visible ? (item.sold ? 0.65 : 1) : 0,
        transform: visible ? (isPressed ? 'scale(0.97)' : 'translateY(0)') : 'translateY(8px)',
        transition: `opacity 0.3s ease ${index * 30}ms, transform 120ms ease, border-color 0.15s`,
        display: 'flex',
        flexDirection: 'column' as const,
        boxShadow: '0 1px 4px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.03)',
      }}
    >
      {/* Content: price, $/hr, title, dimensions */}
      <div style={{ padding: '14px 12px 12px', textAlign: 'center', minWidth: 0 }}>
        <div style={{
          fontSize: 17, fontWeight: 700, color: T.text,
          fontVariantNumeric: 'tabular-nums', fontFamily: T.fontFamily,
          letterSpacing: '-0.02em',
        }}>
          {displayPrice(item)}
        </div>
        {item.effectiveHourly != null && (
          <div style={{ fontSize: 10, fontWeight: 600, color: accent, fontFamily: T.fontFamily, marginTop: 2 }}>
            ${Math.round(item.effectiveHourly / 100)}/hr
          </div>
        )}
        <div style={{
          fontSize: 13, fontWeight: 600, color: T.text, fontFamily: T.fontFamily,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 4,
        }}>
          {title}
        </div>
        <div style={{ fontSize: 10, color: T.textMuted, fontFamily: T.fontFamily, marginTop: 2 }}>
          {item.width}&times;{item.height}&quot; &middot; {relativeTime(item.createdAt)}
        </div>
        {item.hadFreeRoast && (
          <div style={{ fontSize: 11, color: T.primary, fontFamily: T.fontFamily, marginTop: 4, fontWeight: 600 }}>
            ✦ Forensic Roast included
          </div>
        )}
      </div>

      {/* SOLD badge */}
      {item.sold && (
        <div style={{
          position: 'absolute', top: 12, right: 8,
          fontSize: 8, fontWeight: 700, color: T.textMuted,
          background: 'rgba(168,165,160,0.15)',
          padding: '2px 6px', borderRadius: 4,
          textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: T.fontFamily,
        }}>SOLD</div>
      )}

      {/* BELOW badge */}
      {!item.sold && h === 'below' && (
        <div style={{
          position: 'absolute', top: 12, right: 8,
          fontSize: 8, fontWeight: 700, color: '#D64545',
          background: 'rgba(214,69,69,0.1)',
          padding: '2px 6px', borderRadius: 4,
          textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: T.fontFamily,
        }}>BELOW</div>
      )}

      {/* Costs changed dot removed — was causing confusing red dots */}

      {/* Selection checkbox */}
      {selectionMode && (
        <div style={{
          position: 'absolute', top: 8, left: 8, zIndex: 2,
          width: 22, height: 22, borderRadius: 6,
          background: selected ? T.primary : T.white,
          border: selected ? 'none' : `2px solid ${T.textMuted}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 200ms ease',
        }}>
          {selected && (
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path d="M3 7.5l2.5 2.5L11 4" stroke={T.white} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
      )}
    </div>
  );
}
